import {
    Row,
    Col,
    Typography,
    Table,
    Button,
    message,
  } from "antd";
  import { routes } from "common/routes";
  import { generateHeaders } from "common/utils";
  import { AppPageHeader } from "components/AppPageHeader";
  import { useAppDispatch } from "hooks/useAppDispatch";
  import { useAppSelector } from "hooks/useAppSelector";
  import { useDocumentTitle } from "hooks/usePageTitle";
  import { useEffect, useMemo } from "react";
  import { useNavigate } from "react-router-dom";
import { cancelConsumerSocietyClusterUpload, removeProcessingConsumerSocietyUpload, saveConsumerSocietyClusterUpload } from "store/consumer-services/cosumerServicesSlice";
  
  const MESSAGE_KEY = "SAVE_FRANCHISE_MAPPING";
  
  export const SaveConsumerSocietyClusterPage = () => {
    useDocumentTitle("Save Consumer Society Cluster");
    const { processingConsumerSocietyClusterUpload, status, error } = useAppSelector(
      (state) => state.consumerServices
    );
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
      const { timeUploaded } = processingConsumerSocietyClusterUpload;
      if (timeUploaded) {
        const uploadedTime = new Date(timeUploaded).getTime();
        const currentTime = new Date().getTime();
        const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
  
        if (minsPassed >= 15) {
          dispatch(removeProcessingConsumerSocietyUpload());
        }
      }
  
      if (!Object.keys(processingConsumerSocietyClusterUpload).length) { 
        navigate(`/${routes.CONSUMER_SERVICE.UPLOAD_CLUSTER_SOCIETY_MAPPING}`);
      }
    }, [dispatch, processingConsumerSocietyClusterUpload, navigate]);

    useEffect(() => {
      if (status === "loading") {
        message.loading({ content: "saving upload", key: MESSAGE_KEY });
      } else if (status === "success") {
        message.success({
          content: "Consumer Society Cluster Saved!",
          duration: 5,
          key: MESSAGE_KEY,
        });
      } else if (status === "cancelling") {
        message.loading({ content: "cancelling upload", key: MESSAGE_KEY });
      } else if (status === "error") {
        message.error({ content: error, key: MESSAGE_KEY });
      }
  
      return () => {
        message.destroy();
      };
    }, [status, error]);
  
    let content;
    const { headerMapping } = processingConsumerSocietyClusterUpload;
    const { Text } = Typography;
  
    const generateMemoizedHeaders = useMemo(
      () => generateHeaders(headerMapping),
      [headerMapping]
    );
  
    const onSaveRequisition = () => {
      dispatch(saveConsumerSocietyClusterUpload());
    };
  
    const onCancelSave = () => {
      dispatch(cancelConsumerSocietyClusterUpload());
    };
  
    if ((processingConsumerSocietyClusterUpload.success) === 0) {
      content = <Text>Nothing to show here!</Text>;
    } else {
      const headers = generateMemoizedHeaders || {};
      const successRows = processingConsumerSocietyClusterUpload.successRows;
      const data = successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
      const columns = [
        ...Object.keys(headers).map(
          (key) =>
            ({
              key,
              title: headers[key],
              dataIndex: key,
            } as any)
        ),
      ];
      content = (
        <Table
          size="small"
          columns={columns}
          dataSource={data}
          scroll={{ y: 500, x: "100vh" }}
          summary={() => (
            <Table.Summary fixed="bottom">
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={24}>
                  <Row>
                    <Col>
                      <Button
                        type="primary"
                        onClick={onSaveRequisition}
                        loading={status === "loading"}
                        disabled={status === "loading"}
                      >
                        Save Cluster
                      </Button>
                    </Col>
                    <Col style={{ justifyContent: "left", marginLeft: "20px" }}>
                      <Button
                        onClick={onCancelSave}
                        disabled={status === "loading"}
                      >
                        Cancel
                      </Button>
                    </Col>
                  </Row>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        ></Table>
      );
    }
  
    return (
      <>
        <Row>
          <Col>
            <AppPageHeader title="Save Consumer Society Cluster" />
          </Col>
        </Row>
        <Row>
          <Col xs={24}>{content}</Col>
        </Row>
      </>
    );
  };
  